import React, { useState, useEffect, useCallback, useParam } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import QuestionnaireBuilder from "./QuestionnaireBuilder";
import ChatInterface from "./ChatInterface";
import AuthScreen from "./AuthScreen";
import Header from "./Header";
import JobseekerList from "./JobseekerList";
import JobListing from "./JobListing";
import SwimLanes from "./SwimLanes";
import Dashboard from "./Dashboard";
import Billing from "./Billing";
import CompanySettings from "./CompanySettings";
import { NotificationProvider } from "./CustomNotificationSystem";
import ForgotPasswordPage from "./ForgotPassword";
import ResetPasswordPage from "./ResetPassword";
import JobPostForm from "./JobPostForm";
import AssessmentProfile from "./AssessmentProfile";
import AssessmentProfileForm from "./AssessmentProfileNew";
import AssessmentPreferences from "./CompanyAssessmentPreferences";
import WorkerManagement from "./WorkerManagement";
import ReferenceForm from "./RefereceInvite";
import ReferenceReject from "./ReferenceReject";
import JobLisings from "./CompanyJobs";
import JobDetail from "./CompanyJobDetails";
import { api } from "./utils/api";
import { loadJobsJobseekers } from "./utils";
import NotFound from "./NotFound";
import LedaHome from "./leda/Home";
import LedaAbout from "./leda/About";
import BullHornCallback from "./BullHornCallback";
import JobseekerBulkUpload from "./JobseekerBulkUploadRevised";
import SearchJobseekers from "./SearchJobseekers";
import BulkMessageToJobseekerRequest from "./BulkMessageToJobseekerRequest";
import ScheduleInterviewSlots from "./ScheduleInterviewSlots";
import LedaJobListings from "./LedaJobs";
import VideoCallPage from "./VideoCallPage";
import HomePage from "./videoCallPages/HomePage";
import { RoomProvider } from "./context/RoomContext";
import DashboardPage from "./videoCallPages/DashboardPage";
import TranscriptsListPage from "./videoCallPages/TranscriptsListPage";
import TranscriptViewerPage from "./videoCallPages/TranscriptViewerPage";
import AssessmentSetupPage from "./videoCallPages/AssessmentSetupPage";
import AssessmentReportPage from "./videoCallPages/AssessmentReportPage";
import InterviewScheduleManager from "./InterviewScheduleManager";

const CompanyJobSearchWrapper = () => {
  const { companyId } = useParams();
  return <JobLisings companyId={companyId} />;
};

const BulkMessagePageWrapper = () => {
  const { permission, jobId } = useParams();
  return (
    <BulkMessageToJobseekerRequest jobId={jobId} permission={permission} />
  );
};
const CompanyJobDetailWrapper = () => {
  let { job } = useParams();
  const [searchParams] = useSearchParams();

  if (!job) {
    job = searchParams.get("bhjob");
  }

  return <JobDetail jobId={job} />;
};

const ScheduleInterviewSlotsWrapper = () => {
  const { jobseekerId, jobId } = useParams();
  return <ScheduleInterviewSlots jobseekerId={jobseekerId} jobId={jobId} />;
};

// Protected Route Component
const ProtectedRoute = ({ children, isLoggedIn, isLoading }) => {
  const location = useLocation();

  if (isLoading) return <div>Loading...</div>;
  if (!isLoggedIn) {
    // Preserve current path as "next" parameter
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
};

// Auth Routes Component
const AuthRoutes = ({ onLogin, userToken, pageFor }) => {
  const navigate = useNavigate();
  const AuthScreenWrapper = ({ onLogin }) => {
    const [searchParams] = useSearchParams();
    const next = searchParams.get("next");
    const navigate = useNavigate();
    return (
      <AuthScreen
        onLogin={onLogin}
        next={next}
        changeToForgotPassword={() => navigate("/forgot-password")}
      />
    );
  };

  return (
    <Routes>
      <Route path="/" element={<AuthScreenWrapper onLogin={onLogin} />} />
      <Route path="/login" element={<AuthScreenWrapper onLogin={onLogin} />} />
      <Route
        path="/jobseeker/broadcast/:permission/:jobId"
        element={<BulkMessagePageWrapper />}
      />
      <Route path="/job" element={<LedaJobListings />} />
      <Route
        path="/forgot-password"
        element={
          <ForgotPasswordPage changeView={(view) => navigate(`/${view}`)} />
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage token={userToken} />}
      />
      <Route
        path="/chat"
        element={<ChatInterface selectedJob={null} userType={pageFor} />}
      />
      <Route
        path="/reference/invite"
        element={<ReferenceForm token={userToken} />}
      />
      <Route
        path="/reference/reject"
        element={<ReferenceReject token={userToken} />}
      />
      <Route path="/404" element={<NotFound />} />
      <Route path="/leda-group" element={<LedaHome />} />
      <Route path="/leda-group/about" element={<LedaAbout />} />
      <Route path="/:companyId/career" element={<CompanyJobSearchWrapper />} />
      <Route path="/career/job" element={<CompanyJobDetailWrapper />} />
      <Route path="/job/:job" element={<CompanyJobDetailWrapper />} />
      <Route path="/callback/bullhorn" element={<BullHornCallback />} />
      <Route
        path="/schedule/interview/slots/:jobseekerId/:jobId"
        element={<ScheduleInterviewSlotsWrapper />}
      />
      {/* Video Call Routes */}
      <Route
        path="/video-call"
        element={
          <RoomProvider>
            <HomePage />
          </RoomProvider>
        }
      />
      <Route path="/video-call/join/:roomId" element={<VideoCallPage />} />
      <Route path="/video-call/dashboard" element={<DashboardPage />} />
      <Route path="/video-call/transcripts" element={<TranscriptsListPage />} />
      <Route
        path="/video-call/transcript/:roomId"
        element={<TranscriptViewerPage />}
      />
      <Route
        path="/video-call/assessment/setup/:roomId"
        element={<AssessmentSetupPage />}
      />
      <Route
        path="/video-call/assessment/report/:roomId"
        element={<AssessmentReportPage />}
      />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

// Main App Layout Component
const AppLayout = ({
  user,
  jobs,
  jobseekers,
  handleLogout,
  handleJobChange,
  selectedJob,
  company,
  handleJobCreation,
  handleCompanySave,
  setJobseekers,
  fetchJobseekersForJob,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Convert currentView path mappings to match route definitions
  const viewToPath = {
    dashboard: "/",
    createJob: "/create-job",
    createProfile: "/assessment/profile/new",
    assessmentProfile: "/assessment/profile",
    assessmentProfileEdit: "/assessment/profile/:profileId/eidt",
    assessmentPreferenceEdit: "/assessment/preference/edit",
    questionBuilder: "/questionnaire",
    chatInterface: "/chat",
    jobseekerList: "/jobseekers",
    jobListing: "/jobs",
    jobseekerBulkUpload: "/jobseekers/bulk/upload",
    jobseekerserch: "/jobseekers/search",
    swimLanes: "/swimlanes",
    interviewSchedule: "/interview-schedule",
    workerManagement: "/worker-management",
    companysettings: "/company-settings",
    billing: "/billing",
    editJob: "/job/edit/:jobId",
  };

  const handleMenuItemClick = useCallback(
    (view) => {
      const path = viewToPath[view] || "/";
      navigate(path);
    },
    [navigate],
  );

  const getCurrentView = useCallback(() => {
    const path = location.pathname;
    // Remove leading slash and find the corresponding view
    const currentPath = path.substring(1);
    return currentPath || "dashboard";
  }, [location]);

  // Create a wrapper component to handle the parameters
  const JobEditWrapper = ({ company }) => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    //const initialData = await getJobDetails(jobId);
    return (
      <JobPostForm
        company={company}
        setCurrentView={(view) => navigate(viewToPath[view] || "/")}
        setSelectedJob={selectedJob}
        jobId={jobId}
      />
    );
  };

  const AssessmentProfileEditWrapper = ({ user, company }) => {
    const { profileId } = useParams();
    const navigate = useNavigate();
    //const initialData = await getJobDetails(jobId);
    return (
      <AssessmentProfileForm
        company={company}
        currentUser={user}
        setCurrentView={(view) => navigate(viewToPath[view] || "/")}
        profileId={profileId}
      />
    );
  };

  const AssessmentProfileNew = ({ currentUser, company, setCurrentView }) => {
    let duplicateProfileId = null;
    let duplicate = false;

    const [searchParams] = useSearchParams();
    duplicateProfileId = searchParams.get("duplicate");

    if (duplicateProfileId) {
      duplicate = true;
    }
    return (
      <AssessmentProfileForm
        company={company}
        currentUser={currentUser}
        setCurrentView={setCurrentView}
        profileId={duplicateProfileId}
        duplicate={duplicate}
      />
    );
  };

  const AssessmentPreferenceEditWrapper = ({ company }) => {
    const navigate = useNavigate();
    return (
      <AssessmentPreferences
        company={company}
        setCurrentView={(view) => navigate(viewToPath[view] || "/")}
      />
    );
  };

  const BulkUploadWrapper = () => (
    <JobseekerBulkUpload
      jobId={selectedJob}
      currentView={getCurrentView()}
      onJobChange={handleJobChange}
      jobs={jobs}
      onUploadComplete={fetchJobseekersForJob}
    />
  );
  return (
    <div className="App bg-gray-100 min-h-screen">
      <Header
        selectedJob={selectedJob}
        onJobChange={handleJobChange}
        jobs={jobs}
        onMenuItemClick={handleMenuItemClick}
        currentView={getCurrentView()}
        onLogout={handleLogout}
        user={user}
        company={company}
      />
      <div>
        <Routes>
          <Route
            path="/"
            element={
              <Dashboard
                jobs={jobs}
                selectedJob={selectedJob}
                jobseekers={jobseekers}
                company={company}
                onNavigate={(view, jobseekerId) => {
                  const path = viewToPath[view] || "/";
                  navigate(jobseekerId ? `${path}/${jobseekerId}` : path);
                }}
                fetchJobseekersForJob={fetchJobseekersForJob}
                onJobChange={handleJobChange}
              />
            }
          />

          <Route
            path="/create-job"
            element={
              <JobPostForm
                company={company}
                setCurrentView={(view) => navigate(viewToPath[view] || "/")}
                setSelectedJob={handleJobCreation}
              />
            }
          />
          <Route
            path="/assessment/profile"
            element={<AssessmentProfile user={user} />}
          />

          <Route
            path="/job/edit/:jobId"
            element={<JobEditWrapper company={company} />}
          />

          <Route
            path="/questionnaire/:jobId?"
            element={<QuestionnaireBuilder selectedJob={selectedJob} />}
          />
          <Route
            path="/chat"
            element={
              <ChatInterface
                selectedJob={selectedJob}
                onJobChange={handleJobChange}
                jobs={jobs}
                userType="c"
              />
            }
          />

          <Route
            path="/jobseekers/:jobseekerId?"
            element={
              <JobseekerListWrapper
                selectedJob={selectedJob}
                initialJobseekers={jobseekers}
                jobs={jobs}
                currentUser={user}
                currentCompany={company}
              />
            }
          />
          <Route
            path="/jobseekers/bulk/upload"
            element={<BulkUploadWrapper />}
          />

          <Route
            path="/jobseekers/search"
            element={<SearchJobseekers user={user} />}
          />

          <Route
            path="/jobs"
            element={
              <JobListing
                onJobSelected={(jobId) => navigate(`/questionnaire/${jobId}`)}
                onViewJobseekers={(jobId) =>
                  navigate(`/jobseekers?job=${jobId}`)
                }
                onViewSwimLanes={(jobId) => navigate(`/swimlanes/${jobId}`)}
                onEditJob={(jobId) => navigate(`/job/edit/${jobId}`)}
                companyJobs={jobs}
                onMenuItemClick={handleMenuItemClick}
              />
            }
          />

          <Route
            path="/swimlanes/:jobId?"
            element={
              <SwimLanes
                selectedJob={selectedJob}
                jobseekers={jobseekers}
                setJobseekers={setJobseekers}
                onNavigate={(view, jobseekerId) => {
                  const path = viewToPath[view] || "/";
                  navigate(jobseekerId ? `${path}/${jobseekerId}` : path);
                }}
                currentUser={user}
              />
            }
          />

          <Route
            path="/interview-schedule"
            element={<InterviewScheduleManager user={user} company={company} />}
          />

          <Route path="/worker-management" element={<WorkerManagement />} />

          <Route
            path="/company-settings"
            element={
              <CompanySettings
                company={company}
                onSave={handleCompanySave}
                currentUser={user}
              />
            }
          />

          <Route path="/billing" element={<Billing />} />
          <Route
            path="/assessment/profile/new"
            element={
              <AssessmentProfileNew
                company={company}
                currentUser={user}
                setCurrentView={(view) => navigate(viewToPath[view] || "/")}
              />
            }
          />
          <Route
            path="/assessment/profile/:profileId/edit"
            element={
              <AssessmentProfileEditWrapper company={company} user={user} />
            }
          />

          <Route
            path="/assessment/preference/edit"
            element={<AssessmentPreferenceEditWrapper company={company} />}
          />
          <Route
            path="/:companyId/career"
            element={<CompanyJobSearchWrapper />}
          />
          <Route path="/job/:job" element={<CompanyJobDetailWrapper />} />
          <Route path="/career/job" element={<CompanyJobDetailWrapper />} />

          <Route path="/callback/bullhorn" element={<BullHornCallback />} />
          <Route
            path="/jobseeker/broadcast/:permission/:jobId"
            element={<BulkMessagePageWrapper />}
          />
          <Route
            path="/schedule/interview/slots/:jobseekerId/:jobId"
            element={<ScheduleInterviewSlotsWrapper />}
          />
          {/* Video Call Routes */}
          <Route
            path="/video-call"
            element={
              <RoomProvider>
                <HomePage />
              </RoomProvider>
            }
          />
          <Route path="/video-call/join/:roomId" element={<VideoCallPage />} />
          <Route path="/video-call/dashboard" element={<DashboardPage />} />
          <Route
            path="/video-call/transcripts"
            element={<TranscriptsListPage />}
          />
          <Route
            path="/video-call/transcript/:roomId"
            element={<TranscriptViewerPage />}
          />
          <Route
            path="/video-call/assessment/setup/:roomId"
            element={<AssessmentSetupPage />}
          />
          <Route
            path="/video-call/assessment/report/:roomId"
            element={<AssessmentReportPage />}
          />
          {/* Catch all route for invalid paths */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
};

const JobseekerListWrapper = ({
  selectedJob,
  initialJobseekers,
  jobs,
  currentUser,
  currentCompany,
}) => {
  const { jobseekerId } = useParams();

  return (
    <JobseekerList
      selectedJob={selectedJob}
      expandedJobseekerId={jobseekerId}
      initialJobseekers={initialJobseekers}
      jobs={jobs}
      currentUser={currentUser}
      currentCompany={currentCompany}
    />
  );
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentView] = useState("dashboard");
  const [jobs, setJobs] = useState([]);

  const [jobseekers, setJobseekers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  const [company, setCompany] = useState({});
  const [userToken, setUserToken] = useState(null);
  const [pageMode, setPageMode] = useState(null);
  const [pageFor, setPageFor] = useState(null);

  const [selectedJob, setSelectedJob] = useState("");
  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    setIsLoggedIn(false);
    setUser(null);
    setJobs([]);
    setJobseekers([]);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    const mode = searchParams.get("mode");
    const page_for = searchParams.get("for"); // page is for jobseeker or company
    if (token) {
      setUserToken(token);
    }
    if (mode) {
      setPageMode(mode);
    }
    if (page_for) {
      setPageFor(page_for);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/admin/jobs/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching jobs from API:", error);
      throw error;
    }
  }, []);

  const fetchJobseekersForJob = useCallback(async (jobId) => {
    try {
      const data = await loadJobsJobseekers(jobId);
      setJobseekers(data);
    } catch (error) {
      console.error(`Error fetching jobseekers for job ${jobId}:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchInitialData = useCallback(
    async (userData) => {
      setIsLoadingInitialData(true);
      try {
        if (!userData || !userData.company) {
          console.error("No company ID found in user data:", userData);
          return;
        }
        const jobsData = await fetchJobs();
        setJobs(jobsData);
        if (jobsData.length > 0) {
          const searchParams = new URLSearchParams(window.location.search);
          const queryJob = searchParams.get("job");
          let defaultJob = jobsData[0]._id;
          if (queryJob) {
            defaultJob = queryJob;
          }
          setSelectedJob(defaultJob);
          await fetchJobseekersForJob(defaultJob);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setIsLoadingInitialData(false);
      }
    },
    [fetchJobs, fetchJobseekersForJob],
  );

  const handleLogin = async (email, password, next, rememberMe = false) => {
    try {
      const response = await api.post("/api/auth/login", {
        email,
        password,
        rememberMe,
      });
      localStorage.setItem("token", response.data.token);
      setIsLoggedIn(true);
      setUser(response.data.user);
      await fetchInitialData(response.data.user);

      if (next) {
        window.location.href = next; // reload to next page after login if enabled
      } else {
        window.location.href = "/dashboard"; // reload to dashboard page after login if enabled
      }
    } catch (error) {
      alert(error.response.data.message);
      //toast(error.response.data.message);
    }
  };

  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await api.get("/api/auth/check", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.isAuthenticated) {
          setIsLoggedIn(true);
          setUser(response.data.user);
          await fetchInitialData(response.data.user);

          // Check for "next" parameter and redirect if present
          const searchParams = new URLSearchParams(window.location.search);
          const next = searchParams.get("next");
          if (next) {
            window.location.href = next;
            return;
          }
        } else {
          handleLogout();
        }
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error("Error checking auth status:", error);
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchInitialData, handleLogout]);

  const handleCompanySave = async (updatedCompany) => {
    // Handle saving the updated company data
    // This might involve an API call to your backend
    const token = localStorage.getItem("token");
    if (token) {
      const response = await api.put(
        `/api/companies/${company._id}`,
        updatedCompany,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 200) setCompany(updatedCompany);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  useEffect(() => {
    if (selectedJob) {
      fetchJobseekersForJob(selectedJob);
    }
  }, [selectedJob, fetchJobseekersForJob]);

  useEffect(() => {
    const loadCompany = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await api.get("/api/companies/current", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 200) {
          setCompany(response.data);
        }
      }
    };
    if (
      currentView === "companysettings" ||
      currentView === "dashboard" ||
      currentView === "jobseekerList"
    )
      loadCompany();
  }, [currentView]);

  const handleJobChange = useCallback(
    (e) => {
      const newJobId = e.target.value;
      setSelectedJob(newJobId);
      // Ensure jobseekers are fetched for the new job
      fetchJobseekersForJob(newJobId);
    },
    [fetchJobseekersForJob],
  );

  // Add a new function to handle job creation
  const handleJobCreation = useCallback(
    async (jobId) => {
      try {
        // Refresh the jobs list
        const updatedJobs = await fetchJobs();
        setJobs(updatedJobs);
        // Set the new job as selected
        setSelectedJob(jobId);
        // Fetch jobseekers for the new job
        await fetchJobseekersForJob(jobId);
      } catch (error) {
        console.error("Error handling new job creation:", error);
      }
    },
    [fetchJobs, fetchJobseekersForJob, user?.company],
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isLoadingInitialData) {
    return <div>Loading dashboard data...</div>;
  }

  if (!isLoggedIn) {
    return (
      <BrowserRouter>
        <NotificationProvider>
          <AuthRoutes
            onLogin={handleLogin}
            userToken={userToken}
            pageFor={pageFor}
          />
        </NotificationProvider>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <NotificationProvider>
        <ProtectedRoute
          isLoggedIn={isLoggedIn}
          isLoading={isLoading || isLoadingInitialData}
        >
          <AppLayout
            user={user}
            jobs={jobs}
            jobseekers={jobseekers}
            handleLogout={handleLogout}
            handleJobChange={handleJobChange}
            selectedJob={selectedJob}
            company={company}
            handleJobCreation={handleJobCreation}
            handleCompanySave={handleCompanySave}
            setJobseekers={setJobseekers}
            fetchJobseekersForJob={fetchJobseekersForJob}
          />
        </ProtectedRoute>
      </NotificationProvider>
    </BrowserRouter>
  );
}

export default App;
